/**
 * Makes the theme's quick buy work on Variantify's split product cards.
 *
 * Variantify splits one product card into one card per colour, client-side, by
 * cloning the theme's card and rewriting the clone's product link. The clone
 * carries a copy of the theme's <quick-buy-modal>, which the app never touches,
 * so every clone ends up with:
 *
 *   1. the same `handle="...?variant=<first available>"` — the modal always
 *      fetches the default colour no matter which card was clicked; and
 *   2. the same DOM `id` — DialogElement binds a document.body click listener
 *      for `[aria-controls="<id>"]` in connectedCallback, so one "+" click
 *      fires every clone's handler and opens all of them at once.
 *
 * This walks the clones and gives each one's modal a unique id and its own
 * variant, derived from the card's own link.
 */
(function () {
  'use strict';

  var CLONE_SELECTOR = '[data-variantify="clone"], .variantify-clone';
  var BUTTON_SELECTOR = '.product-card__quick-add-button';

  function variantIdFor(card) {
    // Variantify sets this on clones in its default split mode. Its other mode
    // doesn't, so fall back to the link it rewrites on every clone.
    var direct = card.getAttribute('data-variant-id');
    if (direct) return direct;

    var link = card.querySelector('a[href*="variant="]');
    if (!link) return null;

    try {
      return new URL(link.href, window.location.origin).searchParams.get('variant');
    } catch (error) {
      return null;
    }
  }

  function retarget(card) {
    var modal = card.querySelector('quick-buy-modal');
    if (!modal) return;

    var variantId = variantIdFor(card);
    if (!variantId) return;

    if (!modal.dataset.baseId) modal.dataset.baseId = modal.id;

    var targetId = modal.dataset.baseId + '--variant-' + variantId;
    if (modal.id === targetId) return;

    var handle = (modal.getAttribute('handle') || '').split('?')[0];
    if (!handle) return;

    var button =
      card.querySelector(BUTTON_SELECTOR + '[aria-controls="' + CSS.escape(modal.id) + '"]') ||
      card.querySelector(BUTTON_SELECTOR + '[aria-controls]');

    // DialogElement observes `id` but its attributeChangedCallback only handles
    // `open`, so renaming a connected modal leaves the listener bound to the old
    // shared id. Re-inserting it runs connectedCallback again, which does
    // delegate.off().on(...) and rebinds against the unique id.
    var parent = modal.parentNode;
    var anchor = modal.nextSibling;

    modal.remove();
    modal.id = targetId;
    modal.setAttribute('handle', handle + '?variant=' + variantId);
    parent.insertBefore(modal, anchor);

    if (button) button.setAttribute('aria-controls', targetId);
  }

  function sweep() {
    document.querySelectorAll(CLONE_SELECTOR).forEach(retarget);
  }

  // Variantify may rewrite a clone's link after inserting it, so re-sweep once
  // mutations settle. retarget() recomputes the variant every pass and only
  // writes when it differs, so a later pass corrects an earlier misread.
  // setTimeout rather than requestAnimationFrame: rAF is paused in background
  // tabs, so a collection opened in one (cmd-click) would never get swept.
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      sweep();
    }, 0);
  }

  function start() {
    sweep();
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href', 'data-variant-id', 'data-variantify']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
